"use client";

import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { TaskStatus } from "@/types";
import { QuestCard } from "@/app/quests/_components/quest-card";
import { QuestModal } from "@/app/quests/_components/quest-modal";
import { TierSectionHeader } from "@/app/quests/_components/tier-section-header";
import {
  useGetCampaign,
  useGetCampaignTaskStatus,
  useGetCampaignUser,
  useConsumeQuestsArgs,
} from "@/hooks/challenges";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";
import { UserStats } from "./user-stats";
import { useAuth } from "@/hooks/useAuth";
import {
  getUserTier,
  getTaskTier,
  getTaskTiers,
  getTierConfig,
  isTierUnlocked,
  getPreviousTiers,
  getTasksInTier,
  isTierCompleted,
} from "@/config/tiers";
import { areTaskDependenciesMet } from "@/lib/challenges";
import type { TierName } from "@/types/tiers.types";
import { ChallengeCampaignEventState } from "@/types/challenges.types";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";

export function QuestsPage() {
  const [selectedTask, setSelectedTask] = useState<TaskStatus | null>(null);
  const searchParams = useSearchParams();
  const router = useRouter();
  const { address, isAuthenticated, login, token } = useAuth();

  // Handle OAuth redirect parameters (Twitter, Discord, etc.)
  useConsumeQuestsArgs(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);

  const { data: campaign, isLoading: isCampaignLoading } = useGetCampaign(
    CAMPAIGN_ID,
    CAMPAIGN_GROUP_ID
  );
  const { data: taskStatuses, isLoading: isTasksLoading } =
    useGetCampaignTaskStatus(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);
  const { data: challengesUser, isLoading: isChallengesUserLoading } =
    useGetCampaignUser(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);

  console.log("debugging", {
    address,
    isAuthenticated,
    hasToken: !!token,
    isUserLoading: isChallengesUserLoading,
    isCampaignLoading: isCampaignLoading,
    isTasksLoading: isTasksLoading,
  });

  const tasks = taskStatuses?.tasksStatus || [];

  // Handle taskGuid query parameter - only once when data is loaded
  useEffect(() => {
    const taskGuid = searchParams.get("taskGuid");
    if (taskGuid && tasks.length > 0 && !isTasksLoading) {
      const task = tasks.find((t: TaskStatus) => t.taskGuid === taskGuid);
      if (task) {
        setSelectedTask(task);
      }
    }
  }, [searchParams, tasks, isTasksLoading]);

  // Handle opening a task and updating URL
  const handleOpenTask = (task: TaskStatus) => {
    setSelectedTask(task);
    const params = new URLSearchParams(searchParams.toString());
    params.set("taskGuid", task.taskGuid);
    router.replace(`/quests?${params.toString()}`, { scroll: false });
  };

  // Handle modal close to remove query param
  const handleCloseModal = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("taskGuid");

    setSelectedTask(null);
    // Remove taskGuid from URL
    const newUrl = params.toString()
      ? `/quests?${params.toString()}`
      : "/quests";
    router.replace(newUrl, { scroll: false });
  };

  // Calculate user tier and completed tasks
  const userTotalPoints = Number(challengesUser?.totalPoints || 0);
  const completedTaskGuids = tasks
    .filter((t) => t.status === ChallengeCampaignEventState.SUCCESSFUL)
    .map((t) => t.taskGuid);
  const userTier = campaign
    ? getUserTier(completedTaskGuids, tasks, campaign)
    : getUserTier(userTotalPoints);

  // Group tasks by tier based on their maxPoints
  const tierGroups = useMemo(() => {
    const groups: Record<TierName, TaskStatus[]> = {
      TIER1: [],
      TIER2: [],
      TIER3: [],
      AMBASSADOR: [],
    };

    // Filter out disabled tasks
    const enabledTasks = tasks.filter((task: TaskStatus) => task.isEnabled);

    enabledTasks.forEach((task: TaskStatus) => {
      const challengeTask = campaign?.tasks?.find(
        (t) => t.id === task.taskGuid
      );
      const maxPoints = challengeTask?.points
        ? parseFloat(challengeTask.points)
        : parseFloat(task.maxPoints);

      const taskTier = getTaskTier(maxPoints, task.taskGuid);
      groups[taskTier].push(task);
    });

    // Sort tasks within each tier by display order
    Object.keys(groups).forEach((tierKey) => {
      const tierName = tierKey as TierName;
      groups[tierName].sort((a, b) => {
        const aTask = campaign?.tasks?.find((t) => t.id === a.taskGuid);
        const bTask = campaign?.tasks?.find((t) => t.id === b.taskGuid);
        return (aTask?.displayOrder || 0) - (bTask?.displayOrder || 0);
      });
    });

    return groups;
  }, [tasks, campaign]);

  // Get tiers that have tasks (excludes Ambassador)
  const activeTiers = getTaskTiers();

  // Only wait for campaign data to load (public data that doesn't require auth)
  // User-specific data (tasks, user) will load after the page renders
  // This prevents the page from being stuck when auth queries are disabled or slow
  const isLoading = isCampaignLoading;

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-swapper-light flex items-center justify-center px-4">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-swapper-primary/20" />
            <div
              className="absolute inset-0 rounded-full border-4 border-transparent border-t-swapper-primary animate-spin"
              style={{ animationDuration: "0.8s" }}
            />
          </div>
          <p className="text-swapper-secondary/70 font-medium">
            Loading quests...
          </p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-swapper-light flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full text-center">
          <div className="bg-gradient-to-br from-swapper-primary to-[#9F7FFF] p-5 rounded-2xl w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <Star className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-swapper-secondary mb-4">
            Start Your Journey
          </h2>
          <p className="text-swapper-secondary/70 mb-6">
            Connect your wallet to access quests, earn points, and climb the
            leaderboard!
          </p>
          <Button onClick={login} className="w-full">
            Connect Wallet
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* User Stats */}
      <UserStats
        address={address}
        name={challengesUser?.name}
        totalPoints={Number(challengesUser?.totalPoints || 0)}
        leaderboardRank={challengesUser?.rank || 0}
        tasks={tasks}
        campaign={campaign}
        completedTaskGuids={completedTaskGuids}
      />

      {/* Quests Section */}
      <div className="w-full max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-12">
        {/* Show loading skeleton while tasks are loading */}
        {isTasksLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 animate-pulse"
              >
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-4" />
                <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        )}

        {/* Tier-Based Quest Groups */}
        {!isTasksLoading && activeTiers.map((tierName) => {
          const tierTasks = tierGroups[tierName];
          const tierConfig = getTierConfig(tierName);
          const isTierUnlockedForUser = campaign
            ? isTierUnlocked(tierName, completedTaskGuids, tasks, campaign)
            : isTierUnlocked(tierName, userTotalPoints);

          // Skip empty tiers
          if (tierTasks.length === 0) return null;

          const completedTasksInTier = tierTasks.filter((task) =>
            completedTaskGuids.includes(task.taskGuid)
          ).length;

          return (
            <div key={tierName}>
              {/* Tier Section Header */}
              <TierSectionHeader
                tierName={tierName}
                isUnlocked={isTierUnlockedForUser}
                taskCount={tierTasks.length}
                completedTaskCount={completedTasksInTier}
              />

              {/* Ambassador Welcome Message */}
              {tierName === "AMBASSADOR" &&
                isTierUnlockedForUser &&
                tierConfig.welcomeMessage && (
                  <div className="relative mb-8 p-6 md:p-8 bg-[#F1F3FF] rounded-2xl overflow-hidden">
                    {/* Decorative gradient accent */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-swapper-primary via-purple-500 to-swapper-primary" />

                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-swapper-primary to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-swapper-primary/20">
                        <Star className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-swapper-secondary mb-3">
                          Welcome to the Ambassador Tier!
                        </h3>
                        <div className="text-swapper-secondary/70 whitespace-pre-line leading-relaxed">
                          {tierConfig.welcomeMessage
                            .split("\n\n")
                            .slice(1)
                            .join("\n\n")}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/* Quest Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-8">
                {tierTasks.map((task: TaskStatus) => {
                  const challengeTask = campaign?.tasks?.find(
                    (t) => t.id === task.taskGuid
                  );

                  // Tier-based locking: task is locked if any previous tier isn't completed
                  const previousTiers = campaign ? getPreviousTiers(tierName) : [];
                  const incompletePreviousTiers = campaign
                    ? previousTiers.filter((prevTier: TierName) => {
                        const prevTierTasks = getTasksInTier(prevTier, tasks, campaign);
                        return !isTierCompleted(prevTier, completedTaskGuids, prevTierTasks);
                      })
                    : [];

                  const isTaskLockedByTier = incompletePreviousTiers.length > 0;
                  const isTaskLockedByDependency = challengeTask
                    ? !areTaskDependenciesMet(challengeTask, tasks)
                    : false;

                  const isTaskLockedForUser = isTaskLockedByTier || isTaskLockedByDependency;

                  let unlockMessage = "";
                  if (isTaskLockedByTier) {
                    const lastIncompleteTier = incompletePreviousTiers[incompletePreviousTiers.length - 1];
                    const prevTierConfig = getTierConfig(lastIncompleteTier);
                    unlockMessage = `Complete all ${prevTierConfig.displayName} tasks to unlock`;
                  } else if (isTaskLockedByDependency) {
                    unlockMessage = "Complete all other tasks to unlock";
                  }

                  return (
                    <QuestCard
                      key={task.taskGuid}
                      quest={task}
                      challengeTask={challengeTask}
                      onClick={() => handleOpenTask(task)}
                      isLocked={isTaskLockedForUser}
                      unlockMessage={unlockMessage}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quest Modal */}
      <QuestModal
        isOpen={!!selectedTask}
        onClose={handleCloseModal}
        task={selectedTask}
      />
    </div>
  );
}
